import { motion } from "framer-motion";
import NidraMark from "@/components/NidraMark";

const EASE = [0.16, 1, 0.3, 1];

const PERKS = [
  {
    n: "01",
    title: "A permanent founding number",
    body: "Your number is issued once, in order, and never reissued. It is yours for as long as Nostos exists.",
    grad: "linear-gradient(150deg, #E3D5F6 0%, #CBB0EC 100%)",
  },
  {
    n: "02",
    title: "A seat at the table",
    body: "Members help decide what makes it into Nostos. What stays, what goes, what gets better.",
    grad: "linear-gradient(150deg, #FADCE7 0%, #F2B4CB 100%)",
  },
  {
    n: "03",
    title: "First in line",
    body: "Every future drop opens to members before anyone else.",
    grad: "linear-gradient(150deg, #DFE3F8 0%, #C0C9F0 100%)",
  },
  {
    n: "04",
    title: "Founding-member discounts",
    body: "Members receive exclusive pricing, shared directly when each drop opens.",
    grad: "linear-gradient(150deg, #D3E6FA 0%, #A9CDEE 100%)",
  },
  {
    n: "05",
    title: "Community events",
    body: "Invitations to our community events, members first.",
    grad: "linear-gradient(150deg, #FDEFF4 0%, #F6CFDB 100%)",
  },
];

export default function Founding() {
  return (
    <div data-testid="founding-page" className="relative min-h-screen overflow-hidden text-[#171512]">
      <NidraMark className="right-[-6%] top-6" />
      <header className="relative border-b border-[#171512]/10">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6">
          <a href="/" className="font-serif text-2xl italic leading-none">
            nostos
          </a>
          <div className="flex items-center gap-5">
            <a
              data-testid="founding-back-link"
              href="/"
              className="hidden font-mono text-[11px] uppercase tracking-[0.22em] text-[#171512]/50 transition-colors duration-200 hover:text-[#B9822E] sm:block"
            >
              back to nostos.in
            </a>
            <a
              data-testid="founding-join-button"
              href="/#waitlist"
              className="rounded-full bg-[#171512] px-4 py-2 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-[#FDFBF6] transition-colors duration-200 hover:bg-[#F5C7D4] hover:text-[#171512]"
            >
              join now
            </a>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-5xl px-6 py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#B9822E]">
            the founding community
          </p>
          <h1 className="mt-6 font-display text-4xl leading-[1.05] tracking-tight sm:text-6xl">
            what members <span className="font-serif italic">get.</span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-[1.8] text-[#171512]/65">
            The first 200 people who choose the Founding Community become part of it. This page is
            the record of what that means.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
          {PERKS.map((p, i) => (
            <motion.article
              key={p.n}
              data-testid={`perk-${p.n}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 180, damping: 17, delay: 0.05 * i }}
              style={{
                background: p.grad,
                boxShadow: "0 26px 55px -32px rgba(23,21,18,0.28)",
              }}
              className="rounded-3xl border border-white/70 p-7"
            >
              <span className="inline-block font-mono text-[10px] tracking-[0.3em] text-[#171512]/45">
                NO. {p.n}
              </span>
              <h2 className="mt-4 font-serif text-[1.45rem] font-semibold leading-snug">{p.title}</h2>
              <p className="mt-3 text-sm leading-[1.7] text-[#171512]/70">{p.body}</p>
            </motion.article>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            whileHover={{ y: -8 }}
            transition={{ type: "spring", stiffness: 180, damping: 17, delay: 0.25 }}
            style={{
              background: "linear-gradient(150deg, #FDEFF4 0%, #F6D5E2 100%)",
              boxShadow: "0 26px 55px -32px rgba(23,21,18,0.3)",
            }}
            className="flex flex-col justify-between rounded-3xl border border-[#171512]/15 p-7"
          >
            <div>
              <h2 className="font-display text-3xl leading-tight">claim your seat.</h2>
              <p className="mt-3 font-mono text-[10px] tracking-[0.22em] text-[#171512]/60">
                CAPPED AT 200 / NUMBERS NEVER REISSUED
              </p>
            </div>
            <a
              data-testid="founding-cta-button"
              href="/#waitlist"
              className="mt-8 inline-block self-start rounded-full bg-[#171512] px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#FDFBF6] transition-colors duration-200 hover:bg-white hover:text-[#171512]"
            >
              join now
            </a>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
